import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

interface ServiceCard {
  icon: string;
  title: string;
  description: string;
  tags: string[];
}

const services: ServiceCard[] = [
  {
    icon: 'ri-scan-2-line',
    title: '지반조사',
    description: '현장 지반조사 데이터를 AI 기반으로 분석하여 지층 구조 및 지반 특성을 정밀하게 파악합니다.',
    tags: ['SPT', 'CPT', '시추조사'],
  },
  {
    icon: 'ri-sensor-line',
    title: '스마트 계측',
    description: '실시간 계측 데이터를 수집·분석하여 구조물 및 지반의 거동을 지속적으로 모니터링합니다.',
    tags: ['IoT 센서', '실시간 모니터링', '이상 감지'],
  },
  {
    icon: 'ri-shield-check-line',
    title: '정밀 안전진단',
    description: '공공시설물 및 인프라에 대한 정밀안전진단을 수행하고 신뢰성 있는 보고서를 제공합니다.',
    tags: ['안전등급 평가', '내하력 분석', '보수보강'],
  },
  {
    icon: 'ri-brain-line',
    title: 'AI 기반 지반 데이터 분석',
    description: '복잡한 지반 데이터를 생성형 AI가 자동으로 해석하여 전문가 수준의 보고서를 생성합니다.',
    tags: ['AI 자동화', '보고서 생성', '의사결정 지원'],
  },
];

const CoreServicesSection = () => {
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState<number | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-28 md:py-36 bg-white">
      <div className="max-w-7xl mx-auto px-8 md:px-16">
        {/* Section Header */}
        <div
          className={`flex flex-col md:flex-row md:items-end justify-between mb-20 gap-8 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="w-10 h-1 bg-[#E8521A] rounded-full" />
              <span
                className="text-[#E8521A] text-sm font-bold tracking-widest uppercase"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Core Services
              </span>
            </div>
            <h2
              className="text-4xl md:text-5xl font-black text-[#1B2A4A] leading-tight"
              style={{ fontFamily: "'Noto Sans KR', sans-serif", letterSpacing: '-0.03em' }}
            >
              핵심 서비스
            </h2>
          </div>
          <p
            className="text-[#1B2A4A] text-lg max-w-md leading-relaxed font-black"
            style={{ fontFamily: "'Noto Sans KR', sans-serif" }}
          >
            데이터 기반의 정밀한 분석으로<br />
            안전하고 신뢰할 수 있는 결과를 제공합니다.
          </p>
        </div>

        {/* Service Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <div
              key={service.title}
              onMouseEnter={() => setHovered(index)}
              onMouseLeave={() => setHovered(null)}
              className={`relative rounded-2xl p-8 border cursor-pointer transition-all duration-300 ${
                hovered === index
                  ? 'bg-[#1B2A4A] border-[#1B2A4A]'
                  : 'bg-[#F8F9FB] border-transparent hover:border-[#1B2A4A]/10'
              } ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 100 + 200}ms` }}
            >
              {/* Icon */}
              <div
                className={`w-20 h-20 flex items-center justify-center rounded-2xl mb-6 transition-all duration-300 ${
                  hovered === index ? 'bg-[#E8521A]' : 'bg-[#E8521A]/10'
                }`}
              >
                <i
                  className={`${service.icon} text-4xl transition-colors duration-300 ${
                    hovered === index ? 'text-white' : 'text-[#E8521A]'
                  }`}
                  style={{ fontSize: '2rem', lineHeight: 1 }}
                />
              </div>

              {/* Title */}
              <h3
                className={`text-[20px] font-black mb-4 leading-tight transition-colors duration-300 ${
                  hovered === index ? 'text-white' : 'text-[#1B2A4A]'
                }`}
                style={{ fontFamily: "'Noto Sans KR', sans-serif", letterSpacing: '-0.02em' }}
              >
                {service.title}
              </h3>

              {/* Accent line under title */}
              <div
                className={`w-10 h-0.5 rounded-full mb-5 transition-colors duration-300 ${
                  hovered === index ? 'bg-[#E8521A]' : 'bg-[#E8521A]/40'
                }`}
              />

              <p
                className={`text-[15px] leading-relaxed mb-6 font-medium transition-colors duration-300 ${
                  hovered === index ? 'text-white/80' : 'text-[#1B2A4A]/75'
                }`}
                style={{ fontFamily: "'Noto Sans KR', sans-serif" }}
              >
                {service.description}
              </p>
              <div className="flex flex-wrap gap-2">
                {service.tags.map((tag) => (
                  <span
                    key={tag}
                    className={`text-xs px-3 py-1.5 rounded-full font-semibold transition-all duration-300 ${
                      hovered === index
                        ? 'bg-white/15 text-white/85'
                        : 'bg-white border border-gray-200 text-[#1B2A4A]/70'
                    }`}
                    style={{ fontFamily: "'Noto Sans KR', sans-serif" }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div
          className={`mt-16 flex justify-center transition-all duration-700 delay-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <Link
            to="/solution"
            className="group inline-flex items-center gap-3 bg-[#1B2A4A] text-white font-bold text-[17px] px-10 py-5 rounded-full hover:bg-[#243659] transition-all duration-200 whitespace-nowrap"
          >
            전체 솔루션 보기
            <i className="ri-arrow-right-line transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CoreServicesSection;
